import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { User, Account, ImpersonationInfo, LoginCredentials } from '../types/auth'
import { authApi } from '../services/auth'

interface AuthState {
  isLoading: boolean
  isAuthenticated: boolean
  user: User | null
  currentAccount: Account | null
  accounts: Account[]
  needsAccountSelection: boolean
  impersonation: ImpersonationInfo | null
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  selectAccount: (accountId: string) => Promise<{ success: boolean; error?: string }>
  refreshSession: () => Promise<void>
  endImpersonation: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [state, setState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    currentAccount: null,
    accounts: [],
    needsAccountSelection: false,
    impersonation: null,
  })

  const refreshSession = useCallback(async () => {
    try {
      const session = await authApi.getSession()
      setState({
        isLoading: false,
        isAuthenticated: session.authenticated,
        user: session.user || null,
        currentAccount: session.current_account || null,
        accounts: session.accounts || [],
        needsAccountSelection: session.needs_account_selection || false,
        impersonation: session.impersonation || null,
      })
    } catch {
      setState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        currentAccount: null,
        accounts: [],
        needsAccountSelection: false,
        impersonation: null,
      })
    }
  }, [])

  useEffect(() => {
    refreshSession()
  }, [refreshSession])

  const login = useCallback(async (credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authApi.login(credentials)

      if (response.success && response.user) {
        setState(prev => ({
          ...prev,
          isAuthenticated: true,
          user: response.user!,
          accounts: response.accounts || [],
          needsAccountSelection: response.needs_account_selection || false,
        }))
        return { success: true }
      }

      return { success: false, error: response.error || 'Login failed' }
    } catch {
      return { success: false, error: 'An unexpected error occurred' }
    }
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout()
    setState({
      isLoading: false,
      isAuthenticated: false,
      user: null,
      currentAccount: null,
      accounts: [],
      needsAccountSelection: false,
      impersonation: null,
    })
  }, [])

  const selectAccount = useCallback(async (accountId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authApi.selectAccount(accountId)

      if (response.success && response.account) {
        setState(prev => ({
          ...prev,
          currentAccount: response.account!,
          needsAccountSelection: false,
        }))
        return { success: true }
      }

      return { success: false, error: response.error || 'Failed to select account' }
    } catch {
      return { success: false, error: 'An unexpected error occurred' }
    }
  }, [])

  const endImpersonation = useCallback(async () => {
    await authApi.endImpersonation()
    await refreshSession()
  }, [refreshSession])

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    selectAccount,
    refreshSession,
    endImpersonation,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
